/**
 * Servico de WebAuthn/passkeys.
 *
 * Encapsula:
 *  - Chamadas a API (/auth/passkey/*, /passkeys, /invites)
 *  - Chamadas dos primitivos do @simplewebauthn/browser (startAuthentication, startRegistration)
 *
 * Todas as funcoes retornam Promises rejeitadas com Error de mensagem amigavel
 * quando o usuario cancela o prompt do OS ou o browser nao suporta.
 */

import {
  startAuthentication,
  startRegistration,
  browserSupportsWebAuthn,
} from "@simplewebauthn/browser";
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  AuthenticationResponseJSON,
  RegistrationResponseJSON,
} from "@simplewebauthn/browser";

import { api } from "./api";
import type { User, TenantBranding } from "../contexts/AuthContext";

// ============ Tipos ============

export interface PasskeySession {
  token: string;
  user: User;
  tenant?: TenantBranding;
}

export interface Passkey {
  id: string;
  nickname: string;
  deviceType: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface Invite {
  token: string;
  url: string;
  expiresAt: string;
}

/** Resposta de /auth/passkey/register/verify — inclui sessao apenas no fluxo de convite. */
export interface RegisterPasskeyResult {
  id: string;
  nickname: string;
  token?: string;
  user?: User;
  tenant?: TenantBranding;
}

// ============ Helpers ============

/**
 * Verifica se o browser suporta WebAuthn.
 * Nao garante que o dispositivo tem autenticador — so que a API existe.
 */
export function isPasskeySupported(): boolean {
  if (typeof window === "undefined") return false;
  return browserSupportsWebAuthn();
}

/**
 * Traduz erros do WebAuthn para mensagens amigaveis.
 * NotAllowedError = usuario cancelou / timeout do prompt do OS.
 */
function toFriendlyError(err: unknown, fallbackMessage: string): Error {
  if (err instanceof Error) {
    if (err.name === "NotAllowedError") {
      return new Error("PASSKEY_CANCELED");
    }
    if (err.name === "InvalidStateError") {
      return new Error("PASSKEY_ALREADY_REGISTERED");
    }
    return err;
  }
  return new Error(fallbackMessage);
}

// ============ Login ============

/**
 * Fluxo completo de login com passkey:
 *  1. POST /auth/passkey/login/options (com email opcional)
 *  2. startAuthentication(options) — abre prompt do OS
 *  3. POST /auth/passkey/login/verify — devolve token+user
 */
export async function loginWithPasskey(email?: string): Promise<PasskeySession> {
  if (!isPasskeySupported()) {
    throw new Error("PASSKEY_UNSUPPORTED");
  }

  const optionsResponse = await api.post<PublicKeyCredentialRequestOptionsJSON>(
    "/auth/passkey/login/options",
    email ? { email } : {}
  );

  if (!optionsResponse.data) {
    throw new Error("Falha ao obter opcoes de autenticacao");
  }

  let response: AuthenticationResponseJSON;
  try {
    response = await startAuthentication({ optionsJSON: optionsResponse.data });
  } catch (err) {
    throw toFriendlyError(err, "Falha ao autenticar com chave de acesso");
  }

  const verifyResponse = await api.post<PasskeySession>(
    "/auth/passkey/login/verify",
    { response }
  );

  if (!verifyResponse.data) {
    throw new Error("Falha ao verificar chave de acesso");
  }

  return verifyResponse.data;
}

// ============ Registro ============

/**
 * Fluxo completo de registro de passkey:
 *  1. POST /auth/passkey/register/options (com inviteToken opcional + nickname)
 *  2. startRegistration(options) — abre prompt do OS pra criar credencial
 *  3. POST /auth/passkey/register/verify — devolve id/nickname
 *     (e token+user quando fluxo de convite, pra auto-login)
 */
export async function registerPasskey(
  nickname: string,
  inviteToken?: string
): Promise<RegisterPasskeyResult> {
  if (!isPasskeySupported()) {
    throw new Error("PASSKEY_UNSUPPORTED");
  }

  const optionsBody: { nickname: string; inviteToken?: string } = { nickname };
  if (inviteToken) optionsBody.inviteToken = inviteToken;

  const optionsResponse = await api.post<PublicKeyCredentialCreationOptionsJSON>(
    "/auth/passkey/register/options",
    optionsBody
  );

  if (!optionsResponse.data) {
    throw new Error("Falha ao obter opcoes de registro");
  }

  let response: RegistrationResponseJSON;
  try {
    response = await startRegistration({ optionsJSON: optionsResponse.data });
  } catch (err) {
    throw toFriendlyError(err, "Falha ao registrar chave de acesso");
  }

  const verifyBody: {
    response: RegistrationResponseJSON;
    nickname: string;
    inviteToken?: string;
  } = { response, nickname };
  if (inviteToken) verifyBody.inviteToken = inviteToken;

  const verifyResponse = await api.post<RegisterPasskeyResult>(
    "/auth/passkey/register/verify",
    verifyBody
  );

  if (!verifyResponse.data) {
    throw new Error("Falha ao verificar registro de chave de acesso");
  }

  return verifyResponse.data;
}

// ============ CRUD de passkeys ============

export async function listPasskeys(): Promise<Passkey[]> {
  const response = await api.get<Passkey[]>("/passkeys");
  return response.data || [];
}

export async function renamePasskey(
  id: string,
  nickname: string
): Promise<Passkey> {
  const response = await api.put<Passkey>(`/passkeys/${id}`, { nickname });
  if (!response.data) throw new Error("Falha ao renomear chave");
  return response.data;
}

export async function deletePasskey(id: string): Promise<void> {
  await api.delete(`/passkeys/${id}`);
}

// ============ Bootstrap check ============

/**
 * Verifica se o usuario logado ainda nao tem nenhuma passkey cadastrada.
 * Usado pra forcar migracao no primeiro login pos-lancamento do sistema.
 */
export async function checkNeedsBootstrap(): Promise<boolean> {
  const response = await api.get<{ needsBootstrap: boolean }>(
    "/auth/passkey/needs-bootstrap"
  );
  return response.data?.needsBootstrap ?? false;
}

// ============ Convites ============

/**
 * Cria um convite pra novo usuario cadastrar sua primeira passkey.
 * Requer permissao de admin (backend valida).
 */
export async function createInvite(usuarioId: string): Promise<Invite> {
  const response = await api.post<Invite>("/invites", { usuarioId });
  if (!response.data) throw new Error("Falha ao criar convite");
  return response.data;
}

// ============ Nickname helpers ============

/**
 * Sugere um apelido pra passkey baseado no user-agent atual.
 * Nao e cirurgico — so pra deixar um placeholder decente.
 */
export function suggestPasskeyNickname(): string {
  if (typeof navigator === "undefined") return "Este dispositivo";
  const ua = navigator.userAgent;

  let browser = "Navegador";
  if (/Edg\//.test(ua)) browser = "Edge";
  else if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) browser = "Chrome";
  else if (/Firefox\//.test(ua)) browser = "Firefox";
  else if (/Safari\//.test(ua) && !/Chrome/.test(ua)) browser = "Safari";

  let os = "";
  if (/Macintosh|Mac OS X/.test(ua)) os = "Mac";
  else if (/Windows/.test(ua)) os = "Windows";
  else if (/iPhone/.test(ua)) os = "iPhone";
  else if (/iPad/.test(ua)) os = "iPad";
  else if (/Android/.test(ua)) os = "Android";
  else if (/Linux/.test(ua)) os = "Linux";

  return os ? `${browser} no ${os}` : browser;
}
